import { WS_CLOSE_CODE, WS_PATH, type WsMessageType } from 'cpool-constants';

import { APICommunicator } from '~/api/APICommunicator';
import { API } from '~/config';

const WS_URL = `${API.URL.replace(/^http/, 'ws')}${WS_PATH}`;
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

export type WsIncomingMessage = { type?: unknown } & Record<string, unknown>;

export type WsMessageHandler = {
    /** Called for every incoming message whose `type` matches this handler's registration. */
    onMessage: (message: WsIncomingMessage) => void;
    /** Called after every (re)connect so the feature can re-establish its subscriptions. */
    onOpen?: () => void;
};

/**
 * Client for the shared multiplexed `/ws` endpoint; chat threads run their own socket (see
 * `useChatSocket`). Auth rides on the session cookie the browser attaches to the upgrade request —
 * the API is same-host, so there is nothing to pass. Keepalive is a server-driven ping/pong, hence
 * no client-side heartbeat here.
 */
class WsClient {
    #ws: WebSocket | null = null;

    #shouldConnect = false;

    #reconnectAttempts = 0;

    #reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    #handlers = new Map<WsMessageType, WsMessageHandler>();

    #onForbidden: (() => void) | null = null;

    /** Register the handler for a message type, replacing any existing one. */
    registerHandler(type: WsMessageType, handler: WsMessageHandler) {
        this.#handlers.set(type, handler);
    }

    unregisterHandler(type: WsMessageType) {
        this.#handlers.delete(type);
    }

    /**
     * Called when the server closes with `WS_CLOSE_CODE.FORBIDDEN`. That close also stops the
     * reconnect loop, so this is the only notice a feature gets before the next explicit `open()`.
     */
    setOnForbidden(handler: (() => void) | null) {
        this.#onForbidden = handler;
    }

    /** Open the connection (idempotent). */
    open() {
        this.#shouldConnect = true;
        this.#connect();
    }

    /** Close the connection and stop reconnecting until the next `open()`. */
    close() {
        this.#shouldConnect = false;
        if (this.#reconnectTimer) {
            clearTimeout(this.#reconnectTimer);
            this.#reconnectTimer = null;
        }

        const ws = this.#ws;
        this.#ws = null;
        if (!ws) return;

        // detach so a closing socket can never mutate state that now belongs to a newer socket
        // (e.g. React 18 StrictMode's mount → unmount → mount opens, closes, then reopens)
        ws.onmessage = null;
        ws.onclose = null;
        ws.onerror = null;
        if (ws.readyState === WebSocket.CONNECTING) {
            // closing a still-connecting socket logs a console warning; close it cleanly once open
            ws.onopen = () => ws.close();
        } else {
            ws.onopen = null;
            ws.close();
        }
    }

    /** Returns false, dropping the message, if the socket is not currently open. */
    send(message: { type: WsMessageType } & Record<string, unknown>): boolean {
        if (this.#ws?.readyState !== WebSocket.OPEN) return false;
        this.#ws.send(JSON.stringify(message));
        return true;
    }

    #connect() {
        if (!this.#shouldConnect) return;
        if (this.#ws && (this.#ws.readyState === WebSocket.OPEN || this.#ws.readyState === WebSocket.CONNECTING))
            return;

        const ws = new WebSocket(WS_URL);
        this.#ws = ws;
        ws.onopen = () => {
            if (this.#ws !== ws) return; // superseded by a newer socket
            this.#reconnectAttempts = 0;
            this.#handlers.forEach((handler) => handler.onOpen?.());
        };
        ws.onmessage = (event) => {
            if (this.#ws !== ws) return;
            this.#dispatch(event.data);
        };
        ws.onclose = (event) => {
            if (this.#ws !== ws) return; // an old socket closed; don't disturb the current one
            this.#ws = null;
            this.#handleClose(event.code);
        };
        // let onclose drive the reconnect
        ws.onerror = () => ws.close();
    }

    #handleClose(code: number) {
        if (code === WS_CLOSE_CODE.UNAUTHORIZED) {
            // rejected at the upgrade, so retrying is pointless until the next open() after re-login
            this.#shouldConnect = false;
            return;
        }

        if (code === WS_CLOSE_CODE.FORBIDDEN) {
            this.#shouldConnect = false;
            this.#onForbidden?.();
            return;
        }

        if (code === WS_CLOSE_CODE.UNAUTHENTICATED || code === WS_CLOSE_CODE.TOKEN_EXPIRED) {
            APICommunicator.renew()
                .then((renewed) => {
                    if (renewed === true) this.#scheduleReconnect();
                })
                .catch(() => {
                    this.#scheduleReconnect();
                });
            return;
        }

        this.#scheduleReconnect();
    }

    #scheduleReconnect() {
        if (!this.#shouldConnect || this.#reconnectTimer) return;
        const delay = Math.min(RECONNECT_BASE_MS * 2 ** this.#reconnectAttempts, RECONNECT_MAX_MS);
        this.#reconnectAttempts += 1;
        this.#reconnectTimer = setTimeout(() => {
            this.#reconnectTimer = null;
            this.#connect();
        }, delay);
    }

    #dispatch(raw: unknown) {
        let message: WsIncomingMessage;
        try {
            message = JSON.parse(String(raw));
        } catch {
            return;
        }
        const { type } = message;
        if (typeof type !== 'string') return;
        this.#handlers.get(type as WsMessageType)?.onMessage(message);
    }
}

export const wsClient = new WsClient();
